// ------------------------------------------------------------
// Created by Weverton Couto
// Date: 30/01/2025
// For more information, contact: wevertoncouto@coutodev.com.br
// ------------------------------------------------------------

import React, { useEffect, useState, useRef } from "react";
import { Container, Row, Col, Card, Button, Form, Alert, Badge, Spinner, Table } from "react-bootstrap";
import { alterarConfiguracao, pegarConfiguracao } from "../data/api/gsConnectApi";
import { AgendaSelect2Component } from "components/select2/agendaSelect2Component";
import { ProcedimentosSelect2Component } from "components/select2/procedimentosSelect2Component";
import { StatusSelect2Component } from "components/select2/statusSelect2Component";
import { useHistory } from 'react-router-dom';
import Select from 'react-select';

function GatilhosView() {
  const [mensagemSucesso, setMensagemSucesso] = useState('');
  const [mensagemErro, setMensagemErro] = useState('');
  const [gatilhos, setGatilhos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editando, setEditando] = useState(false);
  const [indiceEditando, setIndiceEditando] = useState(null);

  const [tipoGatilhoSelecionado, setTipoGatilhoSelecionado] = useState(null);
  const agendasRef = useRef();
  const procedimentosRef = useRef();
  const statusRef = useRef();
  const mensagemRef = useRef();

  useEffect(() => {
    carregarGatilhos();
  }, []);

  const showSuccess = (message) => {
    setMensagemSucesso(message);
    setTimeout(() => setMensagemSucesso(''), 3000);
  };

  const showError = (message) => {
    setMensagemErro(message);
    setTimeout(() => setMensagemErro(''), 5000);
  };

  const carregarGatilhos = async () => {
    try {
      const dados = await pegarConfiguracao('GATILHOS_CONFIGURADOS');
      const configs = dados?.valor ? JSON.parse(dados.valor) : [];
      setGatilhos(Array.isArray(configs) ? configs : []);
    } catch (error) {
      console.error('Erro ao carregar gatilhos:', error);
      setGatilhos([]);
    }
  };

  const salvarGatilhos = async (novosGatilhos) => {
    try {
      await alterarConfiguracao('GATILHOS_CONFIGURADOS', JSON.stringify(novosGatilhos));
      setGatilhos(novosGatilhos);
    } catch (error) {
      showError('Erro ao salvar gatilhos.');
    }
  };

  const limparFormulario = () => {
    setTipoGatilhoSelecionado(null);
    agendasRef.current?.setValue(null);
    procedimentosRef.current?.setValue(null);
    statusRef.current?.setValue(null);
    if (mensagemRef.current) mensagemRef.current.value = '';
    setEditando(false);
    setIndiceEditando(null);
  };

  const adicionarGatilho = async () => {
    setLoading(true);

    try {
      const tipoSelecionado = tipoGatilhoSelecionado;
      const agendasSelecionadas = agendasRef.current?.getValue() || [];
      const procedimentosSelecionados = procedimentosRef.current?.getValue() || [];
      const statusSelecionados = statusRef.current?.getValue() || [];
      const mensagemConteudo = mensagemRef.current?.value;

      // Validações
      if (!tipoSelecionado || !tipoSelecionado.value) {
        showError('Selecione um tipo de gatilho.');
        return;
      }
      if (agendasSelecionadas.length === 0) {
        showError('Selecione uma agenda.');
        return;
      }
      if (procedimentosSelecionados.length === 0) {
        showError('Selecione um procedimento.');
        return;
      }
      if (tipoSelecionado.value === 'alterarStatus' && statusSelecionados.length === 0) {
        showError('Selecione um status.');
        return;
      }
      if (!mensagemConteudo || mensagemConteudo.trim() === '') {
        showError('Digite uma mensagem.');
        return;
      }

      const novoGatilho = {
        id: `gatilho_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        tipo: tipoSelecionado.value,
        agenda: {
          Nome: agendasSelecionadas[0].label,
          id: agendasSelecionadas[0].value
        },
        procedimentos: procedimentosSelecionados.map(p => ({ Nome: p.label, id: p.value })),
        status: tipoSelecionado.value === 'alterarStatus' ? statusSelecionados.map(s => ({ Nome: s.label, id: s.value })) : [],
        mensagem: mensagemConteudo
      };

      let gatilhosAtualizados;

      if (editando && indiceEditando !== null) {
        // Modo edição - atualizar gatilho existente
        gatilhosAtualizados = [...gatilhos];
        gatilhosAtualizados[indiceEditando] = novoGatilho;
        await salvarGatilhos(gatilhosAtualizados);
        showSuccess('Gatilho atualizado com sucesso!');
      } else {
        // Modo adição - adicionar novo gatilho
        gatilhosAtualizados = [...gatilhos, novoGatilho];
        await salvarGatilhos(gatilhosAtualizados);
        showSuccess('Gatilho adicionado com sucesso!');
      }

      limparFormulario();
      await carregarGatilhos();
    } catch (error) {
      showError('Erro ao adicionar gatilho.');
    } finally {
      setLoading(false);
    }
  };

  const editarGatilho = (gatilho, index) => {
    setEditando(true);
    setIndiceEditando(index);

    const tiposOptions = [
      { value: 'criar', label: 'Ao criar agendamento' },
      { value: 'reagendar', label: 'Ao reagendar' },
      { value: 'alterarStatus', label: 'Ao alterar status específico' }
    ];
    
    const tipo = tiposOptions.find(t => t.value === gatilho.tipo);
    
    setTipoGatilhoSelecionado(tipo);
    if (agendasRef.current && gatilho.agenda) {
      agendasRef.current.setValue([{ value: gatilho.agenda.id, label: gatilho.agenda.Nome }]);
    }
    if (procedimentosRef.current) {
      procedimentosRef.current.setValue(gatilho.procedimentos.map(p => ({ value: p.id, label: p.Nome })));
    }
    if (statusRef.current && gatilho.tipo === 'alterarStatus') {
      statusRef.current.setValue(gatilho.status.map(s => ({ value: s.id, label: s.Nome })));
    }
    if (mensagemRef.current) {
      mensagemRef.current.value = gatilho.mensagem;
    }
  };

  const removerGatilho = async (index) => {
    if (!window.confirm('Tem certeza que deseja excluir este gatilho?')) {
      return;
    }

    const novosGatilhos = [...gatilhos];
    novosGatilhos.splice(index, 1);
    await salvarGatilhos(novosGatilhos);
    showSuccess('Gatilho removido com sucesso!');
    await carregarGatilhos();
  };

  const getVariaveisDisponiveis = () => {
    const tipo = tipoGatilhoSelecionado?.value;
    
    if (tipo === 'alterarStatus') {
      return ['$nomePaciente', '$nomeProcedimento', '$nomeMedico', '$dataAgendamento', '$horarioAgendamento', '$nomeDiaDaSemana', '$statusAtual', '$link'];
    }
    return ['$nomePaciente', '$nomeProcedimento', '$nomeMedico', '$dataAgendamento', '$horarioAgendamento', '$nomeDiaDaSemana', '$link'];
  };

  const getTipoLabel = (tipo) => {
    const tipos = {
      'criar': 'Ao criar agendamento',
      'reagendar': 'Ao reagendar',
      'alterarStatus': 'Ao alterar status específico'
    };
    return tipos[tipo] || tipo;
  };

  const history = useHistory();

  const tiposOptions = [
    { value: 'criar', label: 'Ao criar agendamento' },
    { value: 'reagendar', label: 'Ao reagendar' },
    { value: 'alterarStatus', label: 'Ao alterar status específico' }
  ];

  // Estatísticas
  const stats = {
    criar: gatilhos.filter(g => g.tipo === 'criar').length,
    reagendar: gatilhos.filter(g => g.tipo === 'reagendar').length,
    alterarStatus: gatilhos.filter(g => g.tipo === 'alterarStatus').length
  };
  const totalGatilhos = gatilhos.length;

  return (
    <div style={{backgroundColor: '#f8f9fa', minHeight: '100vh'}}>
      {/* Header seguindo padrão */}
      <div style={{backgroundColor: '#43a047', color: 'white', padding: '12px 20px', fontSize: '16px', fontWeight: '500'}}>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <Button variant="outline-light" size="sm" style={{borderColor: 'white', color: 'white', marginRight: 12}} onClick={() => history.push('/admin/home')}>
              ← Voltar
            </Button>
            <span>Gatilhos</span>
          </div>
          <Badge bg="primary" className="px-3 py-2">
            {totalGatilhos} gatilhos
          </Badge>
        </div>
      </div>

      <Container fluid className="p-3">
        {/* Alerts */}
        {mensagemSucesso && (
          <Alert variant="success" className="mb-3" dismissible onClose={() => setMensagemSucesso('')}>
            ✅ {mensagemSucesso}
          </Alert>
        )}
        {mensagemErro && (
          <Alert variant="danger" className="mb-3" dismissible onClose={() => setMensagemErro('')}>
            ❌ {mensagemErro}
          </Alert>
        )}

        {/* Cards de Estatísticas */}
        <Row className="mb-3">
          {Object.entries(stats).map(([tipo, count]) => (
            <Col xl={3} lg={4} md={6} key={tipo} className="mb-3 ml-3">
              <Card className="text-center h-100" style={{backgroundColor: '#43a047', color: 'white'}}>
                <Card.Body className="py-3">
                  <div style={{fontSize: '2rem', marginBottom: '8px'}}>⚡</div>
                  <h4 className="mb-1">{count}</h4>
                  <small>{getTipoLabel(tipo)}</small>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Card de configuração de gatilho */}
        <div className="bg-white mb-3" style={{border: '1px solid #dee2e6'}}>
          <div style={{backgroundColor: '#f8f9fa', padding: '12px 16px', borderBottom: '1px solid #dee2e6'}}>
            <h6 className="mb-0" style={{color: '#495057', fontWeight: '600'}}>Adicionar Gatilho</h6>
          </div>
          <div className="p-3">
            {/* Indicador de modo de edição */}
            {editando && (
              <Alert variant="info" className="mb-3" style={{fontSize: '13px'}}>
                ✏️ <strong>Modo de edição:</strong> Editando gatilho #{indiceEditando + 1}
                <Button 
                  variant="outline-secondary" 
                  size="sm" 
                  onClick={limparFormulario}
                  style={{float: 'right', fontSize: '11px'}}
                >
                  Cancelar
                </Button>
              </Alert>
            )}

            {/* Formulário */}
            <Row className="mb-3">
              <Col md={3}>
                <Form.Label className="small fw-medium text-muted">Tipo de Gatilho</Form.Label>
                <Select
                  value={tipoGatilhoSelecionado}
                  onChange={setTipoGatilhoSelecionado}
                  options={tiposOptions}
                  placeholder="Selecione o tipo..."
                  styles={{
                    control: (base) => ({
                      ...base,
                      fontSize: '13px',
                      minHeight: '32px'
                    })
                  }}
                />
              </Col>
              <Col md={3}>
                <Form.Label className="small fw-medium text-muted">Agenda</Form.Label>
                <AgendaSelect2Component ref={agendasRef} />
              </Col>
              <Col md={3}>
                <Form.Label className="small fw-medium text-muted">Procedimentos</Form.Label>
                <ProcedimentosSelect2Component ref={procedimentosRef} />
              </Col>
              {tipoGatilhoSelecionado?.value === 'alterarStatus' && (
                <Col md={3}>
                  <Form.Label className="small fw-medium text-muted">Status</Form.Label>
                  <StatusSelect2Component ref={statusRef} />
                </Col>
              )}
            </Row>

            {/* Variáveis disponíveis */}
            <div className="mb-3">
              <small className="text-muted d-block mb-2"><strong>Variáveis disponíveis:</strong></small>
              <div className="d-flex flex-wrap gap-1">
                {getVariaveisDisponiveis().map((variavel, index) => (
                  <Badge key={index} bg="light" text="dark" className="small">{variavel}</Badge>
                ))}
              </div>
            </div>

            {/* Mensagem */}
            <Row className="mb-3">
              <Col md={10}>
                <Form.Label className="small fw-medium text-muted">Mensagem</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  ref={mensagemRef}
                  placeholder="Digite a mensagem..."
                  style={{fontSize: '13px'}}
                />
              </Col>
              <Col md={2}>
                <Form.Label className="small fw-medium text-muted d-block">&nbsp;</Form.Label>
                <Button
                  variant={editando ? "warning" : "success"}
                  onClick={adicionarGatilho}
                  disabled={loading}
                  style={{
                    backgroundColor: editando ? '#ffc107' : '#43a047', 
                    borderColor: editando ? '#ffc107' : '#43a047', 
                    fontSize: '13px', 
                    color: editando ? '#000' : 'white', 
                    width: '100%'
                  }}
                >
                  {loading ? <Spinner size="sm" /> : (editando ? 'Atualizar' : 'Adicionar')}
                </Button>
              </Col>
            </Row>

            {/* Tabela de configurações */}
            {gatilhos.length > 0 && (
              <div className="mt-4">
                <div className="table-responsive">
                  <Table className="mb-0" style={{fontSize: '12px'}}>
                    <thead style={{backgroundColor: '#f8f9fa'}}>
                      <tr>
                        <th style={{padding: '8px 12px', fontWeight: '600', color: '#495057'}}>Tipo</th>
                        <th style={{padding: '8px 12px', fontWeight: '600', color: '#495057'}}>Agenda</th>
                        <th style={{padding: '8px 12px', fontWeight: '600', color: '#495057'}}>Procedimentos</th>
                        <th style={{padding: '8px 12px', fontWeight: '600', color: '#495057'}}>Status</th>
                        <th style={{padding: '8px 12px', fontWeight: '600', color: '#495057'}}>Mensagem</th>
                        <th style={{padding: '8px 12px', fontWeight: '600', color: '#495057', width: '150px'}}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gatilhos.map((gatilho, index) => (
                        <tr key={gatilho.id} style={{borderBottom: '1px solid #f0f0f0'}}>
                          <td style={{padding: '8px 12px', verticalAlign: 'top'}}>{getTipoLabel(gatilho.tipo)}</td>
                          <td style={{padding: '8px 12px', verticalAlign: 'top'}}>
                            {gatilho.agenda?.Nome || '-'}
                          </td>
                          <td style={{padding: '8px 12px', verticalAlign: 'top'}}>
                            {gatilho.procedimentos.map(p => (
                              <Badge key={p.id} bg="secondary" className="me-1" style={{fontSize: '10px'}}>{p.Nome}</Badge>
                            ))}
                          </td>
                          <td style={{padding: '8px 12px', verticalAlign: 'top'}}>
                            {gatilho.status.length > 0 ? (
                              gatilho.status.map(s => (
                                <Badge key={s.id} bg="info" className="me-1" style={{fontSize: '10px'}}>{s.Nome}</Badge>
                              ))
                            ) : (
                              <small className="text-muted">-</small>
                            )}
                          </td>
                          <td style={{padding: '8px 12px', verticalAlign: 'top', maxWidth: '200px'}}>
                            <div style={{
                              background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
                              padding: '6px 8px',
                              borderRadius: '4px',
                              borderLeft: '3px solid #43a047',
                              fontSize: '11px',
                              lineHeight: '1.3',
                              maxHeight: '60px',
                              overflowY: 'auto',
                              wordBreak: 'break-word'
                            }}>
                              {gatilho.mensagem}
                            </div>
                          </td>
                          <td style={{padding: '8px 12px', textAlign: 'center'}}>
                            <div className="d-flex gap-1 justify-content-center">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => editarGatilho(gatilho, index)}
                                disabled={editando}
                                style={{fontSize: '10px', padding: '2px 6px'}}
                                title="Editar"
                              >
                                ✏️
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removerGatilho(index)}
                                disabled={editando}
                                style={{fontSize: '10px', padding: '2px 6px'}}
                                title="Excluir"
                              >
                                🗑️
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </div>

        {gatilhos.length === 0 && (
          <div className="bg-white p-5 text-center" style={{border: '1px solid #dee2e6'}}>
            <p className="text-muted">Nenhum gatilho configurado. Adicione um gatilho acima.</p>
          </div>
        )}
      </Container>
    </div>
  );
}

export default GatilhosView;

